import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import redactionService from '../../services/redactionService';
import RedactionToolbar from './RedactionToolbar';
import RedactionPageCanvas from './RedactionPageCanvas';
import RedactionMarkList from './RedactionMarkList';
import RedactionAutoDetectModal from './RedactionAutoDetectModal';
import RedactionApplyModal from './RedactionApplyModal';

const RedactionEditor = ({ documentId }) => {
  const navigate = useNavigate();

  // Job state
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor state
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [activeTool, setActiveTool] = useState('draw');
  const [activeReasonCode, setActiveReasonCode] = useState(null);
  const [selectedMarkId, setSelectedMarkId] = useState(null);

  // Marks
  const [marks, setMarks] = useState([]);

  // Reason codes
  const [reasonCodes, setReasonCodes] = useState([]);

  // Modals
  const [showAutoDetect, setShowAutoDetect] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [detectLoading, setDetectLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  // Initialize: create or resume job
  const initJob = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for existing jobs
      const listRes = await redactionService.listJobs(documentId);
      const existingJobs = listRes.data;

      let activeJob = existingJobs.find(
        (j) => j.status === 'ready' || j.status === 'preparing'
      );

      if (activeJob) {
        // Resume existing job
        const jobRes = await redactionService.getJob(activeJob.id);
        setJob(jobRes.data);
        setMarks(jobRes.data.marks || []);
      } else {
        // Create new job
        const createRes = await redactionService.createJob(documentId);
        setJob(createRes.data);
      }
    } catch (err) {
      console.error('Failed to initialize redaction job:', err);
      setError(err.response?.data?.detail || 'Failed to start redaction');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    initJob();
  }, [initJob]);

  // Load reason codes
  useEffect(() => {
    const loadReasonCodes = async () => {
      try {
        const res = await redactionService.getReasonCodes();
        setReasonCodes(res.data);
      } catch (err) {
        console.error('Failed to load reason codes:', err);
      }
    };
    loadReasonCodes();
  }, []);

  // Poll for job status when preparing
  useEffect(() => {
    if (!job || job.status !== 'preparing') return;

    const interval = setInterval(async () => {
      try {
        const res = await redactionService.getJob(job.id);
        setJob(res.data);
        if (res.data.status !== 'preparing') {
          setMarks(res.data.marks || []);
        }
      } catch (err) {
        console.error('Poll failed:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [job]);

  // Poll when applying
  useEffect(() => {
    if (!job || job.status !== 'applying') return;

    const interval = setInterval(async () => {
      try {
        const res = await redactionService.getJob(job.id);
        setJob(res.data);
        if (res.data.status !== 'applying') {
          setApplyLoading(false);
        }
      } catch (err) {
        console.error('Poll failed:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [job]);

  // Draw complete handler
  const handleDrawComplete = useCallback(
    async (rect) => {
      if (!job) return;

      try {
        const markData = {
          ...rect,
          mark_type: 'manual',
          reason_code: activeReasonCode,
          is_confirmed: true,
        };

        const res = await redactionService.createMarks(job.id, [markData]);
        setMarks((prev) => [...prev, ...res.data]);
      } catch (err) {
        console.error('Failed to save mark:', err);
      }
    },
    [job, activeReasonCode]
  );

  // Update mark
  const handleUpdateMark = useCallback(
    async (markId, updates) => {
      if (!job) return;
      try {
        const res = await redactionService.updateMark(job.id, markId, updates);
        setMarks((prev) =>
          prev.map((m) => (m.id === markId ? res.data : m))
        );
      } catch (err) {
        console.error('Failed to update mark:', err);
      }
    },
    [job]
  );

  // Delete mark
  const handleDeleteMark = useCallback(
    async (markId) => {
      if (!job) return;
      try {
        await redactionService.deleteMark(job.id, markId);
        setMarks((prev) => prev.filter((m) => m.id !== markId));
        if (selectedMarkId === markId) setSelectedMarkId(null);
      } catch (err) {
        console.error('Failed to delete mark:', err);
      }
    },
    [job, selectedMarkId]
  );

  // Auto-detect
  const handleAutoDetect = useCallback(
    async (patterns) => {
      if (!job) return null;
      try {
        setDetectLoading(true);
        const res = await redactionService.autoDetect(job.id, patterns);
        return res.data;
      } catch (err) {
        console.error('Auto-detect failed:', err);
        return null;
      } finally {
        setDetectLoading(false);
      }
    },
    [job]
  );

  // Add auto-detect matches as marks
  const handleAddDetectedMarks = useCallback(
    async (detectedMarks) => {
      if (!job) return;
      try {
        const markData = detectedMarks.map((m) => ({
          page_number: m.page_number,
          x1: m.x1,
          y1: m.y1,
          x2: m.x2,
          y2: m.y2,
          mark_type: m.mark_type,
          original_text: m.original_text,
          reason_code: m.reason_code,
          detection_pattern: m.detection_pattern,
          confidence: m.confidence,
          is_confirmed: false, // Auto-detected marks start unconfirmed
        }));

        const res = await redactionService.createMarks(job.id, markData);
        setMarks((prev) => [...prev, ...res.data]);
      } catch (err) {
        console.error('Failed to add detected marks:', err);
      }
    },
    [job]
  );

  // Apply redactions
  const handleApply = useCallback(async () => {
    if (!job) return;
    try {
      setApplyLoading(true);
      const res = await redactionService.applyRedactions(job.id);
      setJob((prev) => ({ ...prev, status: res.data.status }));
    } catch (err) {
      console.error('Apply failed:', err);
      setApplyLoading(false);
    }
    setShowApply(false);
  }, [job]);

  // Download redacted
  const handleDownload = useCallback(async () => {
    if (!job) return;
    try {
      const res = await redactionService.getDownloadUrl(job.id);
      window.open(res.data.download_url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [job]);

  const confirmedCount = marks.filter((m) => m.is_confirmed).length;

  // Loading / preparing states
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-yellow-500 mx-auto" />
          <p className="mt-2 text-gray-600">Loading redaction editor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-yellow-600 hover:text-yellow-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (!job) return null;

  if (job.status === 'preparing') {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-yellow-500 mx-auto" />
          <p className="mt-2 text-gray-600">
            Preparing document for redaction...
          </p>
          <p className="text-sm text-gray-400 mt-1">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (job.status === 'failed') {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-2">Redaction job failed</p>
          <p className="text-sm text-gray-500 mb-4">{job.status_message}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-yellow-600 hover:text-yellow-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-100">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200">
        <button
          onClick={() => navigate(-1)}
          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">Document Redaction</h1>
        {job.has_ocr_layer && (
          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
            OCR Applied
          </span>
        )}
        {job.status === 'completed' && (
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
            Completed
          </span>
        )}
        {job.status === 'applying' && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
            <Loader className="h-3 w-3 animate-spin" />
            Applying...
          </span>
        )}
      </div>

      {/* Toolbar */}
      <RedactionToolbar
        currentPage={currentPage}
        totalPages={job.page_count || 1}
        zoom={zoom}
        activeTool={activeTool}
        activeReasonCode={activeReasonCode}
        reasonCodes={reasonCodes}
        markCount={confirmedCount}
        onPageChange={setCurrentPage}
        onZoomChange={setZoom}
        onToolChange={setActiveTool}
        onReasonChange={setActiveReasonCode}
        onAutoDetect={() => setShowAutoDetect(true)}
        onApply={() => setShowApply(true)}
        onDownload={handleDownload}
        isCompleted={job.status === 'completed'}
      />

      {/* Main content: canvas + sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas area */}
        <div className="flex-1 overflow-auto p-4 flex justify-center">
          <RedactionPageCanvas
            jobId={job.id}
            pageNumber={currentPage}
            zoom={zoom}
            marks={marks}
            activeTool={activeTool}
            selectedMarkId={selectedMarkId}
            onDrawComplete={handleDrawComplete}
            onSelectMark={(mark) => setSelectedMarkId(mark ? mark.id : null)}
            onMoveMark={handleUpdateMark}
          />
        </div>

        {/* Sidebar: mark list */}
        <div className="w-72 border-l border-gray-200 bg-white flex flex-col">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              Redaction Marks ({marks.length})
            </h2>
            {marks.length > 0 && confirmedCount < marks.length && (
              <button
                onClick={() => {
                  marks
                    .filter((m) => !m.is_confirmed)
                    .forEach((m) => handleUpdateMark(m.id, { is_confirmed: true }));
                }}
                className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                Confirm All
              </button>
            )}
          </div>
          <RedactionMarkList
            marks={marks}
            reasonCodes={reasonCodes}
            onUpdateMark={handleUpdateMark}
            onDeleteMark={handleDeleteMark}
            onSelectMark={(mark) => {
              setSelectedMarkId(mark.id);
              setCurrentPage(mark.page_number);
            }}
            selectedMarkId={selectedMarkId}
          />
        </div>
      </div>

      {/* Modals */}
      <RedactionAutoDetectModal
        isOpen={showAutoDetect}
        onClose={() => setShowAutoDetect(false)}
        onDetect={handleAutoDetect}
        onAddMatches={handleAddDetectedMarks}
        loading={detectLoading}
      />

      <RedactionApplyModal
        isOpen={showApply}
        onClose={() => setShowApply(false)}
        onConfirm={handleApply}
        marks={marks}
        loading={applyLoading}
      />
    </div>
  );
};

export default RedactionEditor;
